import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { ButtonVariant, Card, Link, Text } from "@merge-api/merge-javascript-shared";
import { UserType } from "../../../../models/Entities";
import useAppContext from "../../../context/useAppContext";
import EmptyStateWrapper from "../../../shared/EmptyStateWrapper";
import { showSuccessToast } from "../../../shared/Toasts";
import { getPaymentMethods, updateDefaultPaymentMethod } from "./BillingAPIClient";
import { BankAccount, BillingPlanTier, CreditCard, PaymentMethod } from "./BillingModels";
import { useLocation } from "react-router-dom";
import FreeToLaunchConfirmModal from "./components/FreeToLaunchConfirmModal";
import { formatSyncFrequncyPlans, getBillingPlanTitle } from "./BillingUtils";
import PaymentMethodRow from "./components/BillingPaymentMethodRow";
import AddBankAccountModal from "./components/AddBankAccountModal";
import DeletePaymentMethodModal from "./components/DeletePaymentMethodModal";
import OldBillingPage from "./OldBillingPage";
import { Plus } from "lucide-react";
import { SkeletonLoader, Button, Tooltip } from "@merge-api/merge-javascript-shared";
import clsx from "clsx";
import useLoadOrganizationBillingPlan from "./hooks/useLoadOrganizationBillingPlan";
import { navigateToAddPaymentPage } from "../../../../router/RouterUtils";
import LinkedAccountsCountsCard from "../../../shared/LinkedAccountsCountsCard";

function BillingPage() {
  // state
  const [showingAddPaymentMethodModal, setShowingAddPaymentMethodModal] = useState(false);
  const [paymentMethodDeletionTarget, setPaymentMethodDeletionTarget] = useState<
    PaymentMethod | undefined
  >();
  const [paymentMethods, setPaymentMethods] = useState<null | PaymentMethod[]>(null);

  const [showFreeToLaunchModal, setShowFreeToLaunchModal] = useState(false);
  const [defaultPaymentMethodBeingSet, setDefaultPaymentMethodBeingSet] =
    useState<null | PaymentMethod>(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const refetch = queryParams.get("refetch");
  const upgraded = queryParams.get("upgraded");

  // hooks
  const { user } = useAppContext();
  const { organizationBillingPlan, setOrganizationBillingPlan, isLoadingOrganizationBillingPlan } =
    useLoadOrganizationBillingPlan();

  enum QueryParamOption {
    True = "true",
  }

  const history = useHistory();

  const organizePaymentMethods = (
    bankAccounts: BankAccount[],
    creditCards: CreditCard[],
  ): PaymentMethod[] => {
    const arr: PaymentMethod[] = [...bankAccounts, ...creditCards]
      .sort((a, b) => a.created_at.localeCompare(b.created_at))
      .sort((a, _) => (a.is_default_method ? -1 : 1));
    return arr;
  };

  const fetchPaymentMethods = (forceRefreshFromStripe: boolean) => {
    getPaymentMethods({
      forceRefreshStripe: forceRefreshFromStripe,
      onSuccess: (data: { bank_accounts: BankAccount[]; credit_cards: CreditCard[] }) => {
        const paymentMethods = organizePaymentMethods(data.bank_accounts, data.credit_cards);
        setPaymentMethods(paymentMethods);
      },
    });
  };

  const setDefaultPaymentMethod = (paymentMethod: PaymentMethod) => {
    setDefaultPaymentMethodBeingSet(paymentMethod);
    updateDefaultPaymentMethod({
      paymentMethod: paymentMethod,
      onSuccess: (data: { bank_accounts: BankAccount[]; credit_cards: CreditCard[] }) => {
        const paymentMethods = organizePaymentMethods(data.bank_accounts, data.credit_cards);
        setPaymentMethods(paymentMethods);
        setDefaultPaymentMethodBeingSet(null);
        showSuccessToast("Successfully updated your default payment method!");
      },
    });
  };

  // load payment methods
  useEffect(() => {
    if (user.type === UserType.admin_with_billing) {
      //on new payment methods being added to stripe, refetch is set to true to get those new payment methods from stripe. otherwise we don't actively fetch new payment methods
      if (refetch && refetch === QueryParamOption.True) {
        fetchPaymentMethods(true);
      }
      if (upgraded && upgraded === QueryParamOption.True) {
        setShowFreeToLaunchModal(true);
      }
      fetchPaymentMethods(false);
    }
  }, [location]);

  const isFreePlan =
    user.organization.organization_billing_plan?.billing_plan.plan_tier ===
    BillingPlanTier.BILLING_PLAN_TIER_FREE;
  const isLaunchPlan =
    user.organization.organization_billing_plan?.billing_plan.plan_tier ===
    BillingPlanTier.BILLING_PLAN_TIER_LAUNCH;

  const syncPlanMap = user.organization.sync_frequency_plans || {};
  const defaultSyncPlan = syncPlanMap["DEFAULT"];

  const isOldBillingPlan =
    user.organization.organization_billing_plan?.billing_plan.plan_tier ===
      BillingPlanTier.BILLING_PLAN_TIER_GROW ||
    user.organization.organization_billing_plan?.billing_plan.plan_tier ===
      BillingPlanTier.BILLING_PLAN_TIER_EXPAND;

  if (isOldBillingPlan || isFreePlan) {
    return <OldBillingPage />;
  } else {
    return (
      <>
        <FreeToLaunchConfirmModal
          open={showFreeToLaunchModal}
          onHide={() => setShowFreeToLaunchModal(false)}
        />
        <AddBankAccountModal
          show={showingAddPaymentMethodModal}
          onHide={() => {
            setShowingAddPaymentMethodModal(false);
            fetchPaymentMethods(true);
          }}
        />
        <DeletePaymentMethodModal
          paymentMethod={paymentMethodDeletionTarget}
          onHide={() => {
            setPaymentMethodDeletionTarget(undefined);
            fetchPaymentMethods(true);
          }}
        />
        <div className="grid grid-cols-4 gap-6 w-full">
          <div className="flex flex-col col-span-4 xl:col-span-1 gap-6">
            <Card className="px-5 py-4 flex flex-col">
              <Text variant="h5" className="mb-2">
                Current plan
              </Text>
              <div className="font-semibold text-[24px] leading-[32px] bg-[linear-gradient(85.42deg,_#0c57da_9.77%,_#669bf9_89.78%)] bg-clip-text text-transparent">
                {!isLoadingOrganizationBillingPlan ? (
                  getBillingPlanTitle(organizationBillingPlan)
                ) : (
                  <SkeletonLoader width={169} height={24} borderRadius={4} />
                )}
              </div>
              <Text>
                <span className="font-semibold">{formatSyncFrequncyPlans(defaultSyncPlan)}</span>{" "}
                sync frequency
              </Text>
            </Card>
            <LinkedAccountsCountsCard />
          </div>
          <div className="col-span-4 xl:col-span-3">
            <Card className="flex flex-col">
              <div className="flex flex-row justify-between items-center py-5 px-6 border-b-[1px] border-gray-10">
                <Text variant="h4">Payment methods</Text>
                {user.type === UserType.admin_with_billing ? (
                  <Button
                    leftIcon={<Plus size={12} />}
                    variant={ButtonVariant.TertiaryWhite}
                    size="sm"
                    onClick={() => {
                      navigateToAddPaymentPage(history, { fromUpgradeButton: false });
                    }}
                  >
                    Payment method
                  </Button>
                ) : (
                  <Tooltip title="You must be an admin to add payment methods">
                    <Button
                      variant={ButtonVariant.TertiaryWhite}
                      size="sm"
                      leftIcon={<Plus size={12} />}
                      disabled
                    >
                      Payment method
                    </Button>
                  </Tooltip>
                )}
              </div>
              {/* Payment method rows render card/bank details and are thus masked at the list */}
              <div className="ph-no-capture list-group list-group-flush px-6">
                {!paymentMethods && user.type === UserType.admin_with_billing ? (
                  <div>
                    {Array(5)
                      .fill(null)
                      .map((_, index) => (
                        <div
                          key={index}
                          className={clsx("flex flex-row justify-between items-center py-5", {
                            "border-b border-gray-10": index !== 4,
                          })}
                        >
                          <SkeletonLoader width={238} height={12} borderRadius={2} />
                          <SkeletonLoader width={182} height={12} borderRadius={2} />
                        </div>
                      ))}
                  </div>
                ) : user.type !== UserType.admin_with_billing ? (
                  <EmptyStateWrapper title="You must be an admin to view payment methods" />
                ) : paymentMethods && paymentMethods.length > 0 ? (
                  paymentMethods.map((paymentMethod) => (
                    <PaymentMethodRow
                      paymentMethod={paymentMethod}
                      billingPlan={organizationBillingPlan}
                      updateBillingPlan={setOrganizationBillingPlan}
                      onClickSetDefault={() => setDefaultPaymentMethod(paymentMethod)}
                      settingDefault={defaultPaymentMethodBeingSet === paymentMethod}
                      setPaymentMethodDeletionTarget={setPaymentMethodDeletionTarget}
                    />
                  ))
                ) : (
                  <EmptyStateWrapper title="No payment methods" />
                )}
              </div>
            </Card>
          </div>
        </div>

        <div className="flex justify-center my-9">
          <Text className="text-center text-gray-40">
            Tired of painless integrations?{" "}
            <Link href="mailto:hello@merge.dev" className="text-red-60">
              Contact us to cancel your plan
            </Link>
          </Text>
        </div>
      </>
    );
  }
}

export default BillingPage;
